import React from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { ReactComponent as AddIcon } from 'assets/icons/add-red.svg'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import { OrderPointForm, OrderPointType } from './OrderPointForm'
import styles from './OrderPointsGroupForm.module.css'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import {
  findNthPointOfType,
  getNumberOfPointAtIndex,
  newCustomIntermediatePointFormValues,
  newCustomsControlPointFormValues,
  newFerryPassagePointFormValues,
  newLoadingDeliveryPointFormValues,
  newUnloadingDeliveryPointFormValues,
  newUnloadingDeliveryPointFormValuesFromLoadingPoint
} from './formValues'
import { useTranslation } from 'react-i18next'
import { OrderPointsSuggestions } from './OrderPointsSuggestions'
import { ORDER_DELIVERY_POINT_READ, ORDER_DELIVERY_POINT_WRITE, useAuth } from 'context/auth'

export function OrderPointsGroupForm ({
  name,
  onFerryPassageChanged,
  onFerryPassageCreated,
  onFerryPassageDeleted
}) {
  const { t } = useTranslation()
  const { hasAnyPrivileges } = useAuth()
  const { values } = useFormikContext()
  const points = values[name]

  const visible = hasAnyPrivileges([ORDER_DELIVERY_POINT_READ])
  if (!visible) return null

  const disabled = !hasAnyPrivileges([ORDER_DELIVERY_POINT_WRITE])

  const isLoadingPointAddRequired = points.filter(p => p.type === OrderPointType.Loading).length === 0
  const isUnloadingPointAddRequired = points.filter(p => p.type === OrderPointType.Unloading).length === 0

  function addLoadingPoint (arrayHelpers) {
    let index = 0
    while (index < points.length && points[index].type !== OrderPointType.Unloading) {
      index++
    }
    const point = newLoadingDeliveryPointFormValues()
    arrayHelpers.insert(index, point)
  }

  function insertLoadingPoint (index, arrayHelpers) {
    const point = newLoadingDeliveryPointFormValues()
    arrayHelpers.insert(index, point)
  }

  function createNewUnloadingPoint () {
    const loadingDeliveryPointsCount = points.filter(p => p.type === OrderPointType.Loading).length
    const unloadingDeliveryPointsCount = points.filter(p => p.type === OrderPointType.Unloading).length
    if (loadingDeliveryPointsCount > unloadingDeliveryPointsCount) {
      const correspondingLoadingDeliveryPoint = findNthPointOfType(points, OrderPointType.Loading, unloadingDeliveryPointsCount + 1)
      return newUnloadingDeliveryPointFormValuesFromLoadingPoint(correspondingLoadingDeliveryPoint)
    } else {
      return newUnloadingDeliveryPointFormValues()
    }
  }

  function addUnloadingPoint (arrayHelpers) {
    const newPoint = createNewUnloadingPoint()
    arrayHelpers.push(newPoint)
  }

  function insertUnloadingPoint (index, arrayHelpers) {
    const newPoint = createNewUnloadingPoint()
    arrayHelpers.insert(index, newPoint)
  }

  const addFerryPassage = arrayHelpers => {
    const point = newFerryPassagePointFormValues()
    arrayHelpers.push(point)
    onFerryPassageCreated(point.id)
  }

  const addFerryPassageOnIndex = (arrayHelpers, index) => {
    const point = newFerryPassagePointFormValues()
    arrayHelpers.insert(index, point)
    onFerryPassageCreated(point.id)
  }

  const addCustomsControl = arrayHelpers => {
    const point = newCustomsControlPointFormValues()
    arrayHelpers.push(point)
  }

  const addCustomsControlOnIndex = (arrayHelpers, index) => {
    const point = newCustomsControlPointFormValues()
    arrayHelpers.insert(index, point)
  }

  const addCustomIntermediatePoint = arrayHelpers => {
    const point = newCustomIntermediatePointFormValues()
    arrayHelpers.push(point)
  }

  function renderFooter (point, index, arrayHelpers) {
    if (disabled) return null

    if (point.type === OrderPointType.Loading) {
      const indexOfLastLoadingPoint = points.map(p => p.type).lastIndexOf(OrderPointType.Loading)
      if (indexOfLastLoadingPoint === index) {
        return (
          <PrimaryButton
            type="button"
            label={t(`form.order.cargo.add.loadingDeliveryPoints`)}
            onClick={() => insertLoadingPoint(index + 1, arrayHelpers)}
            icon={<AddIcon/>}
            className={styles.addDeliveryPointButton}
          />
        )
      }
    }
    if (point.type === OrderPointType.Unloading) {
      const indexOfLastUnloadingPoint = points.map(p => p.type).lastIndexOf(OrderPointType.Unloading)
      if (indexOfLastUnloadingPoint === index) {
        return (
          <PrimaryButton
            type="button"
            label={t(`form.order.cargo.add.unloadingDeliveryPoints`)}
            onClick={() => insertUnloadingPoint(index + 1, arrayHelpers)}
            icon={<AddIcon/>}
            className={styles.addDeliveryPointButton}
          />
        )
      }
    }
    return null
  }

  function isArrowDownDisplayed (index) {
    if (index === points.length - 1) {
      return false
    }
    const curr = points[index]
    const next = points[index + 1]
    return curr.type !== OrderPointType.Loading || next.type !== OrderPointType.Unloading
  }

  function isArrowUpDisplayed (index) {
    if (index === 0) {
      return false
    }
    const curr = points[index]
    const prev = points[index - 1]
    return curr.type !== OrderPointType.Unloading || prev.type !== OrderPointType.Loading
  }

  const onPointMovedUp = (arrayHelpers, index) => {
    arrayHelpers.swap(index - 1, index)
  }

  const onPointMovedDown = (arrayHelpers, index) => {
    arrayHelpers.swap(index, index + 1)
  }

  const onPointRemoved = (arrayHelpers, point, index) => {
    if (point.type === OrderPointType.FerryPassage) {
      onFerryPassageDeleted(point.id)
    }
    arrayHelpers.remove(index)
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t('form.order.cargo.title')}</h2>
      </div>
      <FieldArray
        name={name}
        render={arrayHelpers => (
          <>
            <div className={styles.orderPointsContainer}>
              {points.map((point, index) => (
                  <div key={point.id}>
                    {!disabled && <OrderPointsSuggestions points={points}
                                                          index={index}
                                                          addFerry={index => addFerryPassageOnIndex(arrayHelpers, index)}
                                                          addCustomsControl={index => addCustomsControlOnIndex(arrayHelpers, index)}
                    />
                    }
                    <OrderPointForm
                      key={point.id}
                      name={`${name}.${index}`}
                      number={getNumberOfPointAtIndex(points, index)}
                      arrowUpDisplayed={isArrowUpDisplayed(index)}
                      onArrowUpClick={() => onPointMovedUp(arrayHelpers, index)}
                      arrowDownDisplayed={isArrowDownDisplayed(index)}
                      onArrowDownClick={() => onPointMovedDown(arrayHelpers, index)}
                      onRemoveClick={() => onPointRemoved(arrayHelpers, point, index)}
                      onFerryPassageChanged={onFerryPassageChanged(point.id)}
                      footer={renderFooter(point, index, arrayHelpers)}
                      disabled={disabled}
                    />
                  </div>
                )
              )}
            </div>
            <div className={styles.footer}>
              <ErrorMessage name={name} className={styles.errorMessage}/>
              {
                (isLoadingPointAddRequired || isUnloadingPointAddRequired) && (
                  <div className={styles.formActionsRow}>
                    {
                      isLoadingPointAddRequired && !disabled &&
                      <PrimaryButton
                        type="button"
                        label={t(`form.order.cargo.add.loadingDeliveryPoints`)}
                        onClick={() => addLoadingPoint(arrayHelpers)}
                        icon={<AddIcon/>}
                        className={styles.addDeliveryPointButton}
                      />
                    }
                    {
                      isUnloadingPointAddRequired && !disabled &&
                      <PrimaryButton
                        type="button"
                        label={t(`form.order.cargo.add.unloadingDeliveryPoints`)}
                        onClick={() => addUnloadingPoint(arrayHelpers)}
                        icon={<AddIcon/>}
                        className={styles.addDeliveryPointButton}
                      />
                    }
                  </div>
                )
              }
              {!disabled && <div className={styles.formActionsRow}>
                <PrimaryButton
                  type="button"
                  label={t('form.order.cargo.add.ferryPassage')}
                  onClick={() => addFerryPassage(arrayHelpers)}
                  icon={<AddIcon className={styles.addIntermediatePointIcon}/>}
                  className={styles.addIntermediatePointButton}
                />
                <PrimaryButton
                  type="button"
                  label={t('form.order.cargo.add.customsControl')}
                  onClick={() => addCustomsControl(arrayHelpers)}
                  icon={<AddIcon className={styles.addIntermediatePointIcon}/>}
                  className={styles.addIntermediatePointButton}
                />
                <PrimaryButton
                  type="button"
                  label={t('form.order.cargo.add.customIntermediatePoint')}
                  onClick={() => addCustomIntermediatePoint(arrayHelpers)}
                  icon={<AddIcon className={styles.addIntermediatePointIcon}/>}
                  className={styles.addIntermediatePointButton}
                />
              </div>
              }
            </div>
          </>
        )}
      />
    </div>
  )
}
